/**
 * Lightweight analytics service (US184).
 *
 * Tracks core product events without exposing PII.
 * Events are sent to the backend where they are persisted only when
 * ANALYTICS_ENABLED is set.  Guest sessions use a session_id that is
 * not linked to future registered accounts.
 */
import api from "../api";
import { trackGAEvent } from "./googleAnalytics";

const SESSION_STORAGE_KEY = "pmm_analytics_session_id";

function _generateSessionId(): string {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}

function _getSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sessionId) {
    sessionId = _generateSessionId();
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Track an analytics event.
 *
 * Sends a lightweight payload to the backend analytics endpoint.
 * If GA4 is configured, the event is also forwarded to Google Analytics.
 * No PII (email, names, recipe content) should be included in properties.
 */
export async function track(
  event: string,
  properties?: Record<string, unknown>,
): Promise<void> {
  // Always forward to GA4 if configured (fire-and-forget)
  trackGAEvent(event, properties);

  try {
    // US230: the JWT is in an httpOnly cookie, sent automatically.
    await api.post("/analytics/events", {
      event,
      properties: properties || {},
      session_id: _getSessionId(),
    });
  } catch {
    // Analytics are best-effort; never block the user flow.
  }
}
